import type { MessageConversationResponseType } from "@sellar-io/api-v2/types";
import { type InfiniteData, useInfiniteQuery } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import {
  apiV2AssertResponse,
  apiV2Request,
  getDefaultInfiniteQueryOptionsV2,
} from "../apiV2Request";
import { ServerStateKeys } from "../ServerStateKeys";

const selectConversation = (
  data: InfiniteData<MessageConversationResponseType>,
) => ({
  pages: data.pages.map((page) => page.messages),
  pageParams: data.pageParams,
  readCursor:
    data.pages.find((page) => page.readCursor !== null)?.readCursor ?? null,
});

export const useMessageConversation = (relationshipId: number) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();
  return useInfiniteQuery({
    queryKey: [
      user?.uid,
      business?.id,
      ServerStateKeys.MessageConversation,
      relationshipId,
    ],
    queryFn: ({ pageParam }) =>
      apiV2Request({
        user,
        business,
        method: "get",
        path: `messages/conversations/${relationshipId}`,
        params: { limit: 10, offset: pageParam },
      }).then(
        (response) =>
          apiV2AssertResponse<MessageConversationResponseType>(response).data,
      ),
    select: selectConversation,
    ...getDefaultInfiniteQueryOptionsV2<MessageConversationResponseType>({
      context: { scope: "business", user, business },
      options: {
        limit: 10,
        getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
        retry: (failureCount, error) => {
          const status = error.response?.status;
          return !(status && status >= 400 && status < 500) && failureCount < 2;
        },
      },
    }),
  });
};
